<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import {
    DateFormatter,
    endOfMonth,
    endOfYear,
    getLocalTimeZone,
    parseDate,
    startOfMonth,
    startOfYear,
    today,
} from '@internationalized/date';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { getDatesCookies, setCookie } from '@js/utils/cookies';
import { breakpointsTailwind, useWindowSize } from '@vueuse/core';
import { Motion } from 'motion-v';
import {
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
    RangeCalendarCell,
    RangeCalendarCellTrigger,
    RangeCalendarGrid,
    RangeCalendarGridBody,
    RangeCalendarGridHead,
    RangeCalendarGridRow,
    RangeCalendarHeadCell,
    RangeCalendarHeader,
    RangeCalendarHeading,
    RangeCalendarNext,
    RangeCalendarPrev,
    RangeCalendarRoot,
    SelectContent,
    SelectItem,
    SelectItemText,
    SelectPortal,
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectViewport,
    SwitchRoot,
    SwitchThumb,
} from 'reka-ui';
import { computed, onMounted, ref, watch } from 'vue';

const {
    callback = () => {},
    compare = false,
    comparablePeriods = [
        'custom',
        'previousPeriod',
        'previousYear',
        'previousYearWithSameDate',
    ],
    canSelectToday = false,
    reverse = false,
    disabled = false,
    size = 'small',
    persist = true,
    loading = false,
    compact = false,
} = defineProps({
    callback: {
        type: Function,
        required: false,
    },
    compare: {
        type: Boolean,
        required: false,
    },
    comparablePeriods: {
        type: Array,
        required: false,
    },
    canSelectToday: {
        type: Boolean,
        required: false,
    },
    reverse: {
        type: Boolean,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
    size: {
        type: String,
        required: false,
        validator: (value) => ['small', 'medium'].includes(value),
    },
    persist: {
        type: Boolean,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
    compact: {
        type: Boolean,
        required: false,
    },
});

const timeZone = getLocalTimeZone();

const PERIODS = {
    custom: 'Personnalisé',
    last7Days: '7 derniers jours',
    last30Days: '30 derniers jours',
    last365Days: '365 derniers jours',
    thisMonth: 'Ce mois-ci',
    lastMonth: 'Le mois dernier',
    thisYear: 'Cette année',
    lastYear: "L'année dernière",
};

const COMPARISON_PERIODS = {
    custom: 'Personnalisé',
    previousYear: "L'année précédente",
    previousYearWithSameDate: 'Année précédente (même jours)',
    previousPeriod: 'Période précédente',
};

const periodOptions = Object.entries(PERIODS).map(([value, label]) => ({
    value,
    label,
}));

const comparisonPeriodOptions = computed(() =>
    Object.entries(COMPARISON_PERIODS)
        .filter(([value]) => comparablePeriods.includes(value))
        .map(([value, label]) => ({ value, label })),
);

const { width } = useWindowSize();

const numberOfMonths = computed(() => {
    if (width.value < breakpointsTailwind.lg) {
        return 1;
    }

    if (width.value < breakpointsTailwind.xl) {
        return 2;
    }

    return 3;
});

const longFormatter = new DateFormatter('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
});

const compactFormatter = new DateFormatter('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
});

function formatRangeCompact(range) {
    if (!range?.start || !range?.end) {
        return '';
    }

    return `${compactFormatter.format(range.start.toDate(timeZone))} – ${compactFormatter.format(range.end.toDate(timeZone))}`;
}

function formatRange(range) {
    if (!range?.start || !range?.end) {
        return '';
    }

    return `${longFormatter.format(range.start.toDate(timeZone))} - ${longFormatter.format(range.end.toDate(timeZone))}`;
}

const maxValue = computed(() => {
    const reference = today(timeZone);
    return canSelectToday ? reference : reference.subtract({ days: 1 });
});

function clampRange(range) {
    if (!range?.start || !range?.end) {
        return range;
    }

    const max = maxValue.value;
    let { start, end } = range;

    if (end.compare(max) > 0) {
        end = max;
    }

    if (start.compare(max) > 0) {
        start = max;
    }

    return { start, end };
}

function computePeriodRange(periodKey) {
    const reference = today(timeZone);
    const yesterday = reference.subtract({ days: 1 });

    switch (periodKey) {
        case 'last7Days':
            return { start: reference.subtract({ days: 7 }), end: yesterday };
        case 'last30Days':
            return {
                start: reference.subtract({ months: 1 }).add({ days: 1 }),
                end: yesterday,
            };
        case 'last365Days':
            return {
                start: reference.subtract({ years: 1 }).add({ days: 1 }),
                end: yesterday,
            };
        case 'thisMonth':
            return { start: startOfMonth(reference), end: yesterday };
        case 'lastMonth': {
            const lastMonth = reference.subtract({ months: 1 });
            return {
                start: startOfMonth(lastMonth),
                end: endOfMonth(lastMonth),
            };
        }
        case 'thisYear':
            return { start: startOfYear(reference), end: yesterday };
        case 'lastYear': {
            const lastYear = reference.subtract({ years: 1 });
            return { start: startOfYear(lastYear), end: endOfYear(lastYear) };
        }
        default:
            return null;
    }
}

function daysBetween(start, end) {
    const milliseconds =
        end.toDate(timeZone).getTime() - start.toDate(timeZone).getTime();
    return Math.round(milliseconds / 86_400_000);
}

function sameDayPreviousYear(date) {
    const previous = date.subtract({ years: 1 });

    const diff =
        (date.toDate(timeZone).getDay() -
            previous.toDate(timeZone).getDay() +
            7) %
            7 || 7;

    return previous.add({ days: diff });
}

function computeComparisonRange(comparisonPeriodKey, baseRange) {
    if (!baseRange?.start || !baseRange?.end) {
        return null;
    }

    switch (comparisonPeriodKey) {
        case 'previousYear':
            return {
                start: baseRange.start.subtract({ years: 1 }),
                end: baseRange.end.subtract({ years: 1 }),
            };
        case 'previousYearWithSameDate':
            return {
                start: sameDayPreviousYear(baseRange.start),
                end: sameDayPreviousYear(baseRange.end),
            };
        default: {
            const length = daysBetween(baseRange.start, baseRange.end);
            const end = baseRange.start.subtract({ days: 1 });
            return { start: end.subtract({ days: length }), end };
        }
    }
}

function getComparisonEnabledCookie() {
    const entry = document.cookie
        .split('; ')
        .find((row) => row.startsWith('comparisonEnabled='));

    return entry ? entry.split('=')[1] === 'true' : true;
}

function parseCookieDate(value) {
    if (!value) {
        return null;
    }

    try {
        return parseDate(value);
    } catch {
        return null;
    }
}

const isOpen = ref(false);

const committedPeriod = ref('last30Days');
const committedRange = ref(computePeriodRange('last30Days'));
const committedComparisonPeriod = ref('previousYear');
const committedComparisonRange = ref(
    computeComparisonRange('previousYear', committedRange.value),
);
const committedComparisonEnabled = ref(true);

const draftPeriod = ref(committedPeriod.value);
const draftRange = ref(committedRange.value);
const draftComparisonPeriod = ref(committedComparisonPeriod.value);
const draftComparisonRange = ref(committedComparisonRange.value);
const draftComparisonEnabled = ref(committedComparisonEnabled.value);

const activeCalendar = ref('base');

const canBeCompared = computed(() => compare && draftComparisonEnabled.value);

const committedCanBeCompared = computed(
    () => compare && committedComparisonEnabled.value,
);

function serialize(period, range, comparisonPeriod, comparisonRange, enabled) {
    const comparison =
        compare && enabled && comparisonRange?.start && comparisonRange?.end
            ? `${comparisonPeriod}|${comparisonRange.start}|${comparisonRange.end}`
            : 'off';

    return `${period}|${range?.start ?? ''}|${range?.end ?? ''}|${comparison}`;
}

const hasChanges = computed(
    () =>
        serialize(
            draftPeriod.value,
            draftRange.value,
            draftComparisonPeriod.value,
            draftComparisonRange.value,
            draftComparisonEnabled.value,
        ) !==
        serialize(
            committedPeriod.value,
            committedRange.value,
            committedComparisonPeriod.value,
            committedComparisonRange.value,
            committedComparisonEnabled.value,
        ),
);

const triggerLabel = computed(() =>
    compact
        ? formatRangeCompact(committedRange.value)
        : formatRange(committedRange.value),
);

const triggerComparisonLabel = computed(() => {
    if (!committedCanBeCompared.value) {
        return '';
    }

    return compact
        ? formatRangeCompact(committedComparisonRange.value)
        : formatRange(committedComparisonRange.value);
});

const draftBaseLabel = computed(() => formatRange(draftRange.value));

const draftComparisonLabel = computed(() =>
    formatRange(draftComparisonRange.value),
);

function oppositeRange() {
    return activeCalendar.value === 'base'
        ? draftComparisonRange.value
        : draftRange.value;
}

function isInOppositeRange(date) {
    const opposite = oppositeRange();

    if (!opposite?.start || !opposite?.end) {
        return false;
    }

    return date.compare(opposite.start) >= 0 && date.compare(opposite.end) <= 0;
}

function isOppositeRangeStart(date) {
    const opposite = oppositeRange();
    return !!opposite?.start && date.compare(opposite.start) === 0;
}

function isOppositeRangeEnd(date) {
    const opposite = oppositeRange();
    return !!opposite?.end && date.compare(opposite.end) === 0;
}

function syncComparisonWithBase() {
    if (canBeCompared.value && draftComparisonPeriod.value !== 'custom') {
        draftComparisonRange.value = computeComparisonRange(
            draftComparisonPeriod.value,
            draftRange.value,
        );
    }
}

function onBasePeriodChange(periodKey) {
    draftPeriod.value = periodKey;

    if (periodKey !== 'custom') {
        draftRange.value = computePeriodRange(periodKey);
        syncComparisonWithBase();
        activeCalendar.value = 'base';
    }
}

function onBaseCalendarSelect(value) {
    if (!value?.start || !value?.end) {
        return;
    }

    draftPeriod.value = 'custom';
    syncComparisonWithBase();
}

function onComparisonPeriodChange(periodKey) {
    draftComparisonPeriod.value = periodKey;

    draftComparisonRange.value = computeComparisonRange(
        periodKey,
        draftRange.value,
    );

    activeCalendar.value = periodKey === 'custom' ? 'comparison' : 'base';
}

function onComparisonCalendarSelect(value) {
    if (!value?.start || !value?.end) {
        return;
    }

    draftComparisonPeriod.value = 'custom';
}

watch(draftComparisonEnabled, (enabled) => {
    if (enabled && !draftComparisonRange.value) {
        draftComparisonRange.value = computeComparisonRange(
            draftComparisonPeriod.value,
            draftRange.value,
        );
    }

    if (!enabled) {
        activeCalendar.value = 'base';
    }
});

function syncDraftFromCommitted() {
    draftPeriod.value = committedPeriod.value;
    draftRange.value = committedRange.value;
    draftComparisonPeriod.value = committedComparisonPeriod.value;
    draftComparisonRange.value = committedComparisonRange.value;
    draftComparisonEnabled.value = committedComparisonEnabled.value;
    activeCalendar.value = 'base';
}

watch(isOpen, (open) => {
    if (open) {
        syncDraftFromCommitted();
    }
});

function datesPayload() {
    return {
        startDate: committedRange.value.start.toString(),
        endDate: committedRange.value.end.toString(),
        startDateToCompare: committedComparisonRange.value?.start?.toString(),
        endDateToCompare: committedComparisonRange.value?.end?.toString(),
        comparisonEnabled: committedComparisonEnabled.value,
    };
}

function persistCookies() {
    if (!persist) {
        return;
    }

    setCookie('period', committedPeriod.value);
    setCookie('startDate', committedRange.value.start.toString());
    setCookie('endDate', committedRange.value.end.toString());
    setCookie('periodToCompare', committedComparisonPeriod.value);

    if (committedComparisonRange.value) {
        setCookie(
            'startDateToCompare',
            committedComparisonRange.value.start.toString(),
        );
        setCookie(
            'endDateToCompare',
            committedComparisonRange.value.end.toString(),
        );
    }

    setCookie(
        'comparisonEnabled',
        committedComparisonEnabled.value ? 'true' : 'false',
    );
}

function update() {
    committedPeriod.value = draftPeriod.value;
    committedRange.value = clampRange(draftRange.value);
    committedComparisonPeriod.value = draftComparisonPeriod.value;
    committedComparisonRange.value = draftComparisonRange.value
        ? clampRange(draftComparisonRange.value)
        : null;
    committedComparisonEnabled.value = draftComparisonEnabled.value;

    persistCookies();
    callback(datesPayload());

    isOpen.value = false;
}

function initializeFromCookies() {
    const cookies = getDatesCookies();

    let baseRange = null;
    let basePeriod = 'custom';

    if (
        cookies.period &&
        cookies.period !== 'custom' &&
        PERIODS[cookies.period]
    ) {
        baseRange = computePeriodRange(cookies.period);
        basePeriod = cookies.period;
    } else {
        const start = parseCookieDate(cookies.startDate);
        const end = parseCookieDate(cookies.endDate);

        if (start && end) {
            baseRange = { start, end };
            basePeriod = 'custom';
        }
    }

    if (!baseRange) {
        baseRange = computePeriodRange('last30Days');
        basePeriod = 'last30Days';
    }

    committedPeriod.value = basePeriod;
    committedRange.value = clampRange(baseRange);

    let comparisonPeriod = comparablePeriods.includes('previousYear')
        ? 'previousYear'
        : comparablePeriods[0];
    let comparisonRange = null;

    if (
        cookies.periodToCompare &&
        cookies.periodToCompare !== 'custom' &&
        COMPARISON_PERIODS[cookies.periodToCompare] &&
        comparablePeriods.includes(cookies.periodToCompare)
    ) {
        comparisonPeriod = cookies.periodToCompare;
        comparisonRange = computeComparisonRange(
            comparisonPeriod,
            committedRange.value,
        );
    } else {
        const start = parseCookieDate(cookies.startDateToCompare);
        const end = parseCookieDate(cookies.endDateToCompare);

        if (
            cookies.periodToCompare === 'custom' &&
            comparablePeriods.includes('custom') &&
            start &&
            end
        ) {
            comparisonPeriod = 'custom';
            comparisonRange = { start, end };
        }
    }

    if (!comparisonRange) {
        comparisonRange = computeComparisonRange(
            comparisonPeriod,
            committedRange.value,
        );
    }

    committedComparisonPeriod.value = comparisonPeriod;
    committedComparisonRange.value = comparisonRange;
    committedComparisonEnabled.value = getComparisonEnabledCookie();
}

onMounted(() => {
    initializeFromCookies();
    syncDraftFromCommitted();

    if (!persist) {
        callback(datesPayload());
    }
});
</script>

<template>
    <PopoverRoot v-model:open="isOpen">
        <PopoverTrigger as-child :disabled="disabled">
            <ActionButton
                :callback="() => {}"
                :size="size"
                :right-icon="CaretDownIcon"
                :rotate-icon="isOpen"
                :disabled="disabled || loading"
                type="primary"
                :loading="loading"
            >
                <span
                    v-if="compact"
                    class="flex items-center gap-1.5 whitespace-nowrap"
                >
                    <strong v-if="hasChanges">*</strong>
                    {{ triggerLabel }}

                    <span
                        v-if="committedCanBeCompared"
                        class="text-xs opacity-80"
                    >
                        vs {{ triggerComparisonLabel }}
                    </span>
                </span>

                <span v-else class="text-left">
                    <span class="block whitespace-nowrap">
                        <strong v-if="hasChanges">*</strong>
                        {{ triggerLabel }}
                    </span>

                    <span
                        v-if="committedCanBeCompared"
                        class="mt-0.5 block text-xs whitespace-nowrap opacity-80"
                    >
                        Comparer à {{ triggerComparisonLabel }}
                    </span>
                </span>
            </ActionButton>
        </PopoverTrigger>

        <PopoverPortal>
            <PopoverContent
                :align="'end'"
                :side="reverse ? 'top' : 'bottom'"
                :side-offset="6"
                :collision-padding="8"
                class="z-50 max-w-[calc(100vw-1rem)]"
            >
                <Motion
                    :initial="{ opacity: 0, scale: 0.96, y: reverse ? 4 : -4 }"
                    :animate="{ opacity: 1, scale: 1, y: 0 }"
                    :transition="{ duration: 0.15 }"
                    class="flex max-h-[calc(var(--reka-popper-available-height,100vh)-1rem)] max-w-full flex-col overflow-auto overscroll-contain rounded-sm border border-gray-200 bg-white shadow-md md:flex-row"
                >
                    <div class="p-4">
                        <RangeCalendarRoot
                            v-if="activeCalendar === 'base' || !canBeCompared"
                            v-slot="{ weekDays, grid }"
                            v-model="draftRange"
                            :max-value="maxValue"
                            :number-of-months="numberOfMonths"
                            locale="fr"
                            class="select-none"
                            @update:model-value="onBaseCalendarSelect"
                        >
                            <RangeCalendarHeader
                                class="mb-4 flex items-center justify-between"
                            >
                                <RangeCalendarPrev
                                    class="rounded-sm p-1 duration-150 hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                                >
                                    <CaretLeftIcon class="size-5" />
                                </RangeCalendarPrev>

                                <RangeCalendarHeading
                                    class="font-medium capitalize"
                                />

                                <RangeCalendarNext
                                    class="rounded-sm p-1 duration-150 hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                                >
                                    <CaretRightIcon class="size-5" />
                                </RangeCalendarNext>
                            </RangeCalendarHeader>

                            <div class="flex flex-col gap-4 md:flex-row">
                                <RangeCalendarGrid
                                    v-for="month in grid"
                                    :key="month.value.toString()"
                                    class="border-collapse"
                                >
                                    <RangeCalendarGridHead>
                                        <RangeCalendarGridRow
                                            class="grid grid-cols-7"
                                        >
                                            <RangeCalendarHeadCell
                                                v-for="day in weekDays"
                                                :key="day"
                                                class="font-bolder pb-1 text-xs text-gray-400 capitalize"
                                            >
                                                {{ day }}
                                            </RangeCalendarHeadCell>
                                        </RangeCalendarGridRow>
                                    </RangeCalendarGridHead>

                                    <RangeCalendarGridBody class="grid gap-1">
                                        <RangeCalendarGridRow
                                            v-for="(
                                                weekDates, index
                                            ) in month.rows"
                                            :key="`base-${index}`"
                                            class="grid grid-cols-7"
                                        >
                                            <RangeCalendarCell
                                                v-for="weekDate in weekDates"
                                                :key="weekDate.toString()"
                                                :date="weekDate"
                                                class="text-center"
                                            >
                                                <RangeCalendarCellTrigger
                                                    :day="weekDate"
                                                    :month="month.value"
                                                    :class="{
                                                        'bg-orange-600/10':
                                                            isInOppositeRange(
                                                                weekDate,
                                                            ) &&
                                                            !isOppositeRangeStart(
                                                                weekDate,
                                                            ) &&
                                                            !isOppositeRangeEnd(
                                                                weekDate,
                                                            ),
                                                        'rounded-l-full bg-orange-600 text-white':
                                                            isOppositeRangeStart(
                                                                weekDate,
                                                            ),
                                                        'rounded-r-full bg-orange-600 text-white':
                                                            isOppositeRangeEnd(
                                                                weekDate,
                                                            ),
                                                    }"
                                                    class="flex size-9 cursor-pointer items-center justify-center text-sm duration-150 outline-none hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-300 data-[disabled]:hover:bg-transparent data-[highlighted]:bg-indigo-600/15 data-[outside-view]:invisible data-[selected]:rounded-none data-[selected]:bg-indigo-600/15 data-[selection-end]:rounded-r-full data-[selection-end]:!bg-indigo-600 data-[selection-end]:!text-white data-[selection-start]:rounded-l-full data-[selection-start]:!bg-indigo-600 data-[selection-start]:!text-white"
                                                />
                                            </RangeCalendarCell>
                                        </RangeCalendarGridRow>
                                    </RangeCalendarGridBody>
                                </RangeCalendarGrid>
                            </div>
                        </RangeCalendarRoot>

                        <RangeCalendarRoot
                            v-else
                            v-slot="{ weekDays, grid }"
                            v-model="draftComparisonRange"
                            :max-value="maxValue"
                            :number-of-months="numberOfMonths"
                            locale="fr"
                            class="select-none"
                            @update:model-value="onComparisonCalendarSelect"
                        >
                            <RangeCalendarHeader
                                class="mb-4 flex items-center justify-between"
                            >
                                <RangeCalendarPrev
                                    class="rounded-sm p-1 duration-150 hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                                >
                                    <CaretLeftIcon class="size-5" />
                                </RangeCalendarPrev>

                                <RangeCalendarHeading
                                    class="font-medium capitalize"
                                />

                                <RangeCalendarNext
                                    class="rounded-sm p-1 duration-150 hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                                >
                                    <CaretRightIcon class="size-5" />
                                </RangeCalendarNext>
                            </RangeCalendarHeader>

                            <div class="flex flex-col gap-4 md:flex-row">
                                <RangeCalendarGrid
                                    v-for="month in grid"
                                    :key="month.value.toString()"
                                    class="border-collapse"
                                >
                                    <RangeCalendarGridHead>
                                        <RangeCalendarGridRow
                                            class="grid grid-cols-7"
                                        >
                                            <RangeCalendarHeadCell
                                                v-for="day in weekDays"
                                                :key="day"
                                                class="pb-1 text-xs font-normal text-gray-400 capitalize"
                                            >
                                                {{ day }}
                                            </RangeCalendarHeadCell>
                                        </RangeCalendarGridRow>
                                    </RangeCalendarGridHead>

                                    <RangeCalendarGridBody class="grid gap-1">
                                        <RangeCalendarGridRow
                                            v-for="(
                                                weekDates, index
                                            ) in month.rows"
                                            :key="`comparison-${index}`"
                                            class="grid grid-cols-7"
                                        >
                                            <RangeCalendarCell
                                                v-for="weekDate in weekDates"
                                                :key="weekDate.toString()"
                                                :date="weekDate"
                                                class="text-center"
                                            >
                                                <RangeCalendarCellTrigger
                                                    :day="weekDate"
                                                    :month="month.value"
                                                    :class="{
                                                        'bg-indigo-600/10':
                                                            isInOppositeRange(
                                                                weekDate,
                                                            ) &&
                                                            !isOppositeRangeStart(
                                                                weekDate,
                                                            ) &&
                                                            !isOppositeRangeEnd(
                                                                weekDate,
                                                            ),
                                                        'rounded-l-full bg-indigo-600/30':
                                                            isOppositeRangeStart(
                                                                weekDate,
                                                            ),
                                                        'rounded-r-full bg-indigo-600/30':
                                                            isOppositeRangeEnd(
                                                                weekDate,
                                                            ),
                                                    }"
                                                    class="flex size-9 cursor-pointer items-center justify-center text-sm duration-150 outline-none hover:bg-gray-100 data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-300 data-[disabled]:hover:bg-transparent data-[highlighted]:bg-orange-600/15 data-[outside-view]:invisible data-[selected]:rounded-none data-[selected]:bg-orange-600/15 data-[selection-end]:rounded-r-full data-[selection-end]:!bg-orange-600 data-[selection-end]:!text-white data-[selection-start]:rounded-l-full data-[selection-start]:!bg-orange-600 data-[selection-start]:!text-white"
                                                />
                                            </RangeCalendarCell>
                                        </RangeCalendarGridRow>
                                    </RangeCalendarGridBody>
                                </RangeCalendarGrid>
                            </div>
                        </RangeCalendarRoot>
                    </div>

                    <div
                        class="flex w-full min-w-0 flex-col gap-4 border-t border-gray-200 p-4 md:w-72 md:shrink-0 md:border-t-0 md:border-l"
                    >
                        <SwitchRoot
                            v-if="compare"
                            v-model="draftComparisonEnabled"
                            class="group flex w-full cursor-pointer items-center justify-between gap-2 text-sm outline-none"
                        >
                            <span>Activer la comparaison</span>

                            <span
                                class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-gray-200 transition-colors duration-150 group-data-[state=checked]:bg-blue-600"
                            >
                                <SwitchThumb
                                    class="block size-3.5 translate-x-1 rounded-full bg-white shadow-sm transition-transform duration-150 group-data-[state=checked]:translate-x-[18px]"
                                />
                            </span>
                        </SwitchRoot>

                        <div class="flex flex-col gap-2">
                            <span class="text-sm font-medium">Période</span>

                            <SelectRoot
                                :model-value="draftPeriod"
                                @update:model-value="onBasePeriodChange"
                            >
                                <SelectTrigger
                                    class="flex min-w-0 items-center justify-between rounded-sm border border-gray-300 px-3 py-2 text-left text-sm ring-1 ring-transparent duration-150 outline-none data-[state=open]:ring-gray-300"
                                >
                                    <SelectValue class="truncate" />

                                    <CaretDownIcon
                                        class="ml-2 size-4 text-gray-500 duration-150"
                                    />
                                </SelectTrigger>

                                <SelectPortal>
                                    <SelectContent
                                        position="popper"
                                        :side-offset="4"
                                        class="z-[70] w-[var(--reka-select-trigger-width)] overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                                        @close-auto-focus.prevent
                                    >
                                        <SelectViewport
                                            class="max-h-60 overflow-y-auto"
                                        >
                                            <SelectItem
                                                v-for="option in periodOptions"
                                                :key="option.value"
                                                :value="option.value"
                                                class="flex h-[28px] cursor-pointer items-center px-3 py-1 text-sm duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white data-[state=checked]:bg-indigo-600 data-[state=checked]:text-white"
                                            >
                                                <SelectItemText>
                                                    {{ option.label }}
                                                </SelectItemText>
                                            </SelectItem>
                                        </SelectViewport>
                                    </SelectContent>
                                </SelectPortal>
                            </SelectRoot>
                        </div>

                        <button
                            type="button"
                            class="h-[38px] truncate rounded-sm border px-3 py-2 text-left text-sm duration-150"
                            :class="
                                activeCalendar === 'base'
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-gray-300 hover:bg-gray-50'
                            "
                            @click="activeCalendar = 'base'"
                        >
                            {{ draftBaseLabel }}
                        </button>

                        <template v-if="canBeCompared">
                            <div class="flex flex-col gap-2">
                                <span class="text-sm font-medium">
                                    Comparer à
                                </span>

                                <SelectRoot
                                    :model-value="draftComparisonPeriod"
                                    :disabled="
                                        comparisonPeriodOptions.length <= 1
                                    "
                                    @update:model-value="
                                        onComparisonPeriodChange
                                    "
                                >
                                    <SelectTrigger
                                        class="flex min-w-0 items-center justify-between rounded-sm border border-gray-300 px-3 py-2 text-left text-sm ring-1 ring-transparent duration-150 outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-60 data-[state=open]:ring-gray-300"
                                    >
                                        <SelectValue class="truncate" />

                                        <CaretDownIcon
                                            class="ml-2 size-4 text-gray-500 duration-150"
                                        />
                                    </SelectTrigger>

                                    <SelectPortal>
                                        <SelectContent
                                            position="popper"
                                            :side-offset="4"
                                            class="z-[70] w-[var(--reka-select-trigger-width)] overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                                            @close-auto-focus.prevent
                                        >
                                            <SelectViewport
                                                class="max-h-60 overflow-y-auto"
                                            >
                                                <SelectItem
                                                    v-for="option in comparisonPeriodOptions"
                                                    :key="option.value"
                                                    :value="option.value"
                                                    class="flex h-[28px] cursor-pointer items-center px-3 py-1 text-sm duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white data-[state=checked]:bg-indigo-600 data-[state=checked]:text-white"
                                                >
                                                    <SelectItemText>
                                                        {{ option.label }}
                                                    </SelectItemText>
                                                </SelectItem>
                                            </SelectViewport>
                                        </SelectContent>
                                    </SelectPortal>
                                </SelectRoot>
                            </div>

                            <button
                                type="button"
                                class="h-[38px] truncate rounded-sm border px-3 py-2 text-left text-sm duration-150"
                                :class="[
                                    activeCalendar === 'comparison'
                                        ? 'border-orange-600 text-orange-600'
                                        : 'border-gray-300 hover:bg-gray-50',
                                    draftComparisonPeriod !== 'custom'
                                        ? 'cursor-not-allowed opacity-70'
                                        : '',
                                ]"
                                :disabled="draftComparisonPeriod !== 'custom'"
                                @click="activeCalendar = 'comparison'"
                            >
                                {{ draftComparisonLabel }}
                            </button>
                        </template>

                        <ActionButton
                            :callback="update"
                            :disabled="!hasChanges"
                            class="mt-auto w-full"
                        >
                            Mettre à jour
                        </ActionButton>
                    </div>
                </Motion>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>
